import React, { forwardRef } from 'react';
import type { PressableProps, ViewProps } from 'react-native';
import { Pressable, StyleSheet, View } from 'react-native';
import { Colors } from '../../constants/Colors';
import { useDynamicStyles } from '../../hooks/useDynamicStyles';

type TableRowProps = ViewProps & {
  children: React.ReactNode;
  onPress?: () => void;
  pressable?: boolean;
};

/**
 * TableRow - Table row container
 *
 * Wraps TableHead or TableCell components. Use in both header and body.
 * Set onPress or pressable={true} to make the row interactive.
 *
 * @example Basic row
 * ```tsx
 * <TableRow>
 *   <TableCell>John Doe</TableCell>
 *   <TableCell>john@example.com</TableCell>
 * </TableRow>
 * ```
 *
 * @example Interactive row
 * ```tsx
 * <TableRow onPress={() => navigate(`/user/${user.id}`)}>
 *   <TableCell>{user.name}</TableCell>
 *   <TableCell>{user.email}</TableCell>
 * </TableRow>
 * ```
 */
export const TableRow = forwardRef<View, TableRowProps>(
  ({ children, style, onPress, pressable, ...props }, ref) => {
    const styles = useDynamicStyles(createStyles);

    if (onPress || pressable) {
      return (
        <Pressable
          ref={ref as React.Ref<View>}
          onPress={onPress}
          style={({ pressed }) => [
            styles.row,
            pressed && styles.rowPressed,
            style,
          ]}
          {...(props as PressableProps)}
        >
          {children}
        </Pressable>
      );
    }

    return (
      <View ref={ref} style={[styles.row, style]} {...props}>
        {children}
      </View>
    );
  },
);

TableRow.displayName = 'TableRow';

const createStyles = () =>
  StyleSheet.create({
    row: {
      flexDirection: 'row',
      width: '100%',
      borderBottomWidth: 1,
      borderBottomColor: Colors.opacity10,
      minHeight: 52,
      alignItems: 'center',
    },
    rowPressed: {
      backgroundColor: Colors.opacity10,
    },
  });
